export type ValidationErrors = {
  clientName: string;
  facilityAddressId: string;
  region: string;
  accountNumber: string;
  mrn: string;
  sendTo: string;
  requestType: string;
  firstName: string;
  lastName: string;
  payerName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
};

export type ValidationField = keyof ValidationErrors;

export type ClientValidationConfig = {
  requiredFields: ValidationField[];
  requireOneOf?: ValidationField[][];
};

export type InventoryValidationData = {
  clientName: string;
  facilityAddressId: number;
  region: string;
  accountNumber: string;
  mrn: string;
  sendTo: string;
  requestType: string;
  firstName: string;
  lastName: string;
  payerName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
};

export const ADDRESS_REQUIRED_SEND_TO = ['Payer', 'Homeless', 'Override Address'];
export const OVERRIDE_ADDRESS_REQUIRED_FIELDS: ValidationField[] = ['firstName', 'lastName'];

const BASE_REQUIRED_FIELDS: ValidationField[] = ['clientName', 'facilityAddressId', 'region', 'sendTo', 'requestType'];

const DEFAULT_CLIENT_VALIDATION_CONFIG: ClientValidationConfig = {
  requiredFields: [...BASE_REQUIRED_FIELDS, 'accountNumber', 'mrn'],
};

export const CLIENT_VALIDATION_CONFIGS: Record<string, ClientValidationConfig> = {
  Dignity: DEFAULT_CLIENT_VALIDATION_CONFIG,
};

const FIELD_LABELS: Record<ValidationField, string> = {
  clientName: 'Client Name',
  facilityAddressId: 'Facility Address',
  region: 'Region',
  accountNumber: 'Account Number',
  mrn: 'MRN',
  sendTo: 'Send To',
  requestType: 'Request Type',
  firstName: 'First Name',
  lastName: 'Last Name',
  payerName: 'Payer Name',
  address: 'Address',
  city: 'City',
  state: 'State',
  zip: 'Zip code',
};

export const createEmptyValidationErrors = (): ValidationErrors => ({
  clientName: '',
  facilityAddressId: '',
  region: '',
  accountNumber: '',
  mrn: '',
  sendTo: '',
  requestType: '',
  firstName: '',
  lastName: '',
  payerName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
});

export const getClientValidationConfig = (clientName: string): ClientValidationConfig => (
  CLIENT_VALIDATION_CONFIGS[clientName] || DEFAULT_CLIENT_VALIDATION_CONFIG
);

export const getValidationErrors = (
  data: InventoryValidationData,
  config: ClientValidationConfig,
): ValidationErrors => {
  const errors = createEmptyValidationErrors();
  const trimmedValues: Record<ValidationField, string> = {
    clientName: data.clientName.trim(),
    facilityAddressId: data.facilityAddressId > 0 ? String(data.facilityAddressId) : '',
    region: data.region.trim(),
    accountNumber: data.accountNumber.trim(),
    mrn: data.mrn.trim(),
    sendTo: data.sendTo.trim(),
    requestType: data.requestType.trim(),
    firstName: data.firstName.trim(),
    lastName: data.lastName.trim(),
    payerName: data.payerName.trim(),
    address: data.address.trim(),
    city: data.city.trim(),
    state: data.state.trim(),
    zip: data.zip.trim(),
  };

  config.requiredFields.forEach((field) => {
    if (!trimmedValues[field]) {
      errors[field] = `${FIELD_LABELS[field]} is required`;
    }
  });

  config.requireOneOf?.forEach((fields) => {
    const hasValue = fields.some((field) => trimmedValues[field]);
    if (!hasValue) {
      const messageText = `${FIELD_LABELS[fields[0]]} or ${FIELD_LABELS[fields[1]]} is required`;
      fields.forEach((field) => {
        errors[field] = messageText;
      });
    }
  });

  if (ADDRESS_REQUIRED_SEND_TO.includes(trimmedValues.sendTo)) {
    if (trimmedValues.sendTo === 'Payer' && !trimmedValues.payerName) {
      errors.payerName = 'Payer Name is required';
    }
    if (!trimmedValues.address) {
      errors.address = 'Address is required';
    }
    if (!trimmedValues.city) {
      errors.city = 'City is required';
    }
    if (!trimmedValues.state) {
      errors.state = 'State is required';
    } else if (!/^[A-Z]{2}$/.test(trimmedValues.state)) {
      errors.state = 'State name is invalid. Maxlength allowed is 2 alphabets in UPPERCASE';
    }
    if (!trimmedValues.zip) {
      errors.zip = 'Zip code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(trimmedValues.zip)) {
      errors.zip = 'Zip code is invalid. Please use ##### or #####-#### format.';
    }
  }

  if (trimmedValues.sendTo === 'Override Address') {
    OVERRIDE_ADDRESS_REQUIRED_FIELDS.forEach((field) => {
      if (!trimmedValues[field]) {
        errors[field] = `${FIELD_LABELS[field]} is required`;
      }
    });
  }

  return errors;
};
